import { MousePointer2 } from 'lucide-react';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { useEffect, useState, type JSX } from 'react';

const NUDGE_SCENE_BROWSE_DURATION_MS = 3500;
const NUDGE_SCENE_OPENED_DURATION_MS = 4500;
const NUDGE_SCENE_CURSOR_CYCLE_S = 8;
/** ease-in-out-cubic */
const NUDGE_SCENE_CURSOR_EASE = [0.645, 0.045, 0.355, 1] as const;

function NudgeCursor(): JSX.Element {
  return (
    <MousePointer2
      className="size-4 text-text-primary"
      fill="currentColor"
      strokeWidth={1.5}
    />
  );
}

export function NudgeScene(): JSX.Element {
  const shouldReduceMotion = useReducedMotion();
  const [animatedPhase, setAnimatedPhase] = useState<'browse' | 'opened'>('browse');

  useEffect(() => {
    if (shouldReduceMotion) return undefined;

    const duration =
      animatedPhase === 'browse'
        ? NUDGE_SCENE_BROWSE_DURATION_MS
        : NUDGE_SCENE_OPENED_DURATION_MS;
    const timer = window.setTimeout(() => {
      setAnimatedPhase((curr) => (curr === 'browse' ? 'opened' : 'browse'));
    }, duration);
    return () => window.clearTimeout(timer);
  }, [animatedPhase, shouldReduceMotion]);

  const phase = shouldReduceMotion ? 'opened' : animatedPhase;

  return (
    <div
      aria-hidden
      className="relative flex h-[108px] w-[180px] shrink-0 flex-col overflow-hidden rounded-lg bg-surface-page"
    >
      <div className="flex items-center gap-1 bg-background-tertiary px-3 py-2">
        <div className="size-1.5 rounded-full bg-entity-neutral" />
        <div className="size-1.5 rounded-full bg-entity-neutral" />
        <div className="size-1.5 rounded-full bg-entity-neutral" />
      </div>

      <div className="relative flex-1">
        <AnimatePresence initial={false}>
          {phase === 'browse' && (
            <m.div
              key="browse"
              className="absolute inset-0 flex flex-col gap-3 px-3 pt-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <div className="flex justify-center gap-2">
                <div className="size-6 rounded-sm bg-border" />
                <div className="size-6 rounded-sm bg-border" />
                <div className="size-6 rounded-sm bg-border" />
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="h-1 w-4/5 rounded-sm bg-border" />
                <div className="h-1 w-3/5 rounded-sm bg-border" />
              </div>
            </m.div>
          )}

          {phase === 'opened' && (
            <m.div
              key="opened"
              className="absolute inset-0 flex flex-col gap-2 px-3 pt-3"
              initial={shouldReduceMotion ? false : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: NUDGE_SCENE_CURSOR_EASE }}
            >
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-3 rounded-sm bg-border" />
                <div className="h-3 flex-1 rounded-sm bg-surface-elevated" />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-3 rounded-sm bg-border" />
                <div className="h-3 flex-1 rounded-sm bg-surface-elevated" />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-3 rounded-sm bg-border" />
                <div className="h-3 flex-1 rounded-sm bg-surface-elevated" />
              </div>
            </m.div>
          )}
        </AnimatePresence>

        {shouldReduceMotion ? (
          <div className="absolute" style={{ top: 38, left: 70 }}>
            <NudgeCursor />
          </div>
        ) : (
          <m.div
            className="absolute"
            style={{ top: -12, left: 160 }}
            animate={{
              x: [
                0, 0, -38, -38, -70, -70, -70, -90, -90, -90, -90, -90, -90, 0, 0, 0, 0,
              ],
              y: [0, 0, 36, 36, 36, 36, 36, 30, 30, 50, 50, 70, 70, 0, 0, 0, 0],
              scale: [1, 1, 1, 1, 1, 0.85, 1, 1, 1, 1, 1, 1, 1, 1, 0.85, 1, 1],
            }}
            transition={{
              duration: NUDGE_SCENE_CURSOR_CYCLE_S,
              times: [
                0, 0.05, 0.15, 0.22, 0.35, 0.4, 0.43, 0.52, 0.58, 0.64, 0.7, 0.76, 0.82,
                0.92, 0.95, 0.97, 1,
              ],
              ease: NUDGE_SCENE_CURSOR_EASE,
              repeat: Infinity,
              repeatType: 'loop',
            }}
          >
            <NudgeCursor />
          </m.div>
        )}
      </div>
    </div>
  );
}
